import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import GenericForm, { FormField } from "../../../components/GenericForm";
import { useQuery } from "@tanstack/react-query";
import { useAlert } from "../../../common/contexts/AlertContext";
import {getAllSites, updateSite } from "../../../common/api/siteService";
import PopUp from "../../../components/PatientCharts/PopUp";
import Loader from "../../../components/Loader/Loader";

const EditSite = () => {
  const location = useLocation();
  const site = location.state?.user; // Access the user data from state
  console.log("site:", site);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alert = useAlert();
  const [pendingSwitch, setPendingSwitch] = useState<{
    fieldName: string;
    checked: boolean;
  } | null>(null);
  const [showPopup, setShowPopup] = useState(false);

  const { isLoading: isDataFetching, error: fetchError, data: siteData } = useQuery({
    queryKey: ["centerData"],
    queryFn: getAllSites,
  });

  let initialFields: FormField[] = [
    // Center Admin Section
    {
      label: "Center Name",
      name: "centerName",
      type: "text",
      initialValue: site?.SiteName || "",
    },
    {
      label: "Country/Region*",
      name: "country",
      type: "text",
      initialValue: site?.country || "",
    },
    {
      label: "City",
      name: "city",
      type: "text",
      initialValue: site?.city || "",
    },
    {
      label: "Street",
      name: "street",
      type: "text",
      initialValue: site?.street || "",
    },
    {
      label: "Apt, suite, etc (optional)",
      name: "apartment",
      type: "text",
      initialValue: site?.apartment || "",
    },
    {
      label: "ZIP/Postcode",
      name: "zipCode",
      type: "text",
      initialValue: site?.zipCode || "",
    },
    {
      label: "Active / Non-Active",
      name: "centerStatus",
      type: "switch",
      initialValue: site?.centerStatus === "Active" ? true : false,
    },
    // Divider and "Contact Person" Title
    { label: "Center Admin", name: "contactSection", type: "section" },

    // "Contact Person" Section
    {
      label: "First Name*",
      name: "contactFirstName",
      type: "text",
      initialValue: site?.firstName || "",
    },
    {
      label: "Last Name*",
      name: "contactLastName",
      type: "text",
      initialValue: site?.lastName || "",
    },
    {
      label: "Email*",
      name: "contactEmail",
      type: "email",
      initialValue: site?.email || "",
    },
    {
      label: "Phone Number*",
      name: "phoneNumber",
      type: "phoneInput",
      initialValue: site?.phoneNumber || "",
    },
  ];
  const [fields, setFields] = useState<FormField[]>(initialFields);

  const getText = (text: string) => t(`createSite.${text}`);

  const handleSwitchChange = (fieldName: string, checked: boolean) => {
    if (fieldName === "centerStatus" && checked === false) {
      setPendingSwitch({ fieldName, checked });
      setShowPopup(true);
    } else {
      updateFieldValue(fieldName, checked);
    }
  };

  const updateFieldValue = (fieldName: string, value: any) => {
    const updatedFields = fields.map((field) => {
      if (field.name === fieldName) {
        return { ...field, value };
      }
      return field;
    });
    setFields(updatedFields);
  };
  const handleConfirm = () => {
    if (pendingSwitch) {
      updateFieldValue(pendingSwitch.fieldName, pendingSwitch.checked);
    }
    setShowPopup(false);
    setPendingSwitch(null);
  };

  const handleCancel = () => {
    if (pendingSwitch) {
      updateFieldValue(pendingSwitch.fieldName, true);
  }
    setShowPopup(false);
    setPendingSwitch(null);
  };
  const handleSubmit = async (formData: { [key: string]: any }) => {
    console.log("Form submitted with data:", formData);
    const centerStatusAsString = formData.centerStatus ? "Active" : "Inactive";
    const res = await updateSite({
      siteName: formData.centerName,
      id: site.id,
      centerStatus: centerStatusAsString,
      siteAdminEmail: formData.contactEmail,
      siteAdminFirstName: formData.contactFirstName,
      siteAdminLastName: formData.contactLastName,
      country: formData.country,
      city: formData.city,
      zipCode: formData.zipCode,
      apartment: formData.apartment,
      street: formData.street,
      phoneNumber: formData.phoneNumber,
    });

    if (res.success) {
      alert("Success", "msg");
      navigate(-1);
    } else {
      alert(res.err, "error");
    }
  };

  useEffect(() => {
    if (Array.isArray(siteData)) {
      const updatedFields = fields.map((field) => {
        if (field.name === "center") {
          return {
            ...field,
            options: siteData
              .filter((site: any) => site && site.SiteName) // Ensure site and SiteName exist
              .map((site: any) => ({
                label: site.SiteName,
                value: site.SiteName,
              })),
          };
        }
        return field;
      });
      setFields(updatedFields);
    }
  }, [siteData]);

  if (isDataFetching) {
    return <Loader text="Loading..." />;
  }

  if (fetchError) {
    return <div>{getText("Error fetching centers")}</div>;
  }

  return (
    <>
      <GenericForm
        backButtonAction={() => navigate(-1)}
        backButtonText={getText("Back")}
        buttonText={getText("Save_changes")}
        fields={fields}
        onSubmit={handleSubmit}
        title={getText("edit_site")}
        onSwitchChange={handleSwitchChange}
      />
      {showPopup && (
        <PopUp
          message={getText("popUp_message")}
          open={showPopup}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

export default EditSite;
